import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthFrontendApis } from "@propelauth/frontend-apis-react";
import { useAuthInfo, useOrgHelper } from "@propelauth/react";
import type {
  ApiKeyExpirationOption,
  OrgApiKey,
  PersonalApiKey,
} from "@propelauth/frontend-apis";
import { Copy, Eye, EyeOff, Plus, Trash2 } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";
import { toast } from "sonner";

type KeyScope = "personal" | "org";

type KeyRow = {
  rowId: string;
  apiKeyId: string;
  scope: KeyScope;
  name: string;
  createdAt: number;
  expiresAtSeconds: number | null;
};

const EXPIRATION_OPTIONS: Array<{ value: ApiKeyExpirationOption; label: string }> = [
  { value: "TwoWeeks", label: "2 weeks" },
  { value: "OneMonth", label: "1 month" },
  { value: "ThreeMonths", label: "3 months" },
  { value: "SixMonths", label: "6 months" },
  { value: "OneYear", label: "1 year" },
  { value: "Never", label: "Never" },
];

const formatDate = (epochSeconds?: number | null) => {
  if (!epochSeconds) return "Never";
  const date = new Date(epochSeconds * 1000);
  if (Number.isNaN(date.valueOf())) return "—";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const maskKey = (apiKeyId: string) => {
  if (!apiKeyId) return "••••••••";
  if (apiKeyId.length <= 10) return `${apiKeyId.slice(0, 2)}••••${apiKeyId.slice(-2)}`;
  return `${apiKeyId.slice(0, 6)}••••••••${apiKeyId.slice(-4)}`;
};

const ApiKeysSettingsPage = () => {
  const authApis = useAuthFrontendApis();
  const { orgHelper } = useOrgHelper();
  const { accessHelper } = useAuthInfo();
  const workspace = useWorkspaceStore((state) => state.workspace);

  const canRead = accessHelper?.hasPermission(workspace?.id, "can_read_api_keys") ?? false;
  const canWrite = accessHelper?.hasPermission(workspace?.id, "can_write_api_keys") ?? false;

  const orgId = useMemo(() => {
    const uniqueIdentifier = workspace?.unique_identifier;
    if (!uniqueIdentifier) return null;
    const orgs: any[] = orgHelper?.getOrgs?.() ?? [];
    const match = orgs.find((org: any) => org?.orgMetadata?.unique_identifier === uniqueIdentifier);
    return match?.orgId ?? null;
  }, [orgHelper, workspace?.unique_identifier]);

  const [personalKeys, setPersonalKeys] = useState<PersonalApiKey[]>([]);
  const [orgKeys, setOrgKeys] = useState<OrgApiKey[]>([]);
  const [loading, setLoading] = useState(false);
  const [showKeys, setShowKeys] = useState<Set<string>>(new Set());

  const [createOpen, setCreateOpen] = useState(false);
  const [scope, setScope] = useState<KeyScope>("personal");
  const [expiration, setExpiration] = useState<ApiKeyExpirationOption>("OneMonth");

  const [createdTokenOpen, setCreatedTokenOpen] = useState(false);
  const [createdToken, setCreatedToken] = useState<{ id: string; token: string } | null>(null);

  useEffect(() => {
    if (orgId) {
      setScope("org");
    } else {
      setScope("personal");
    }
  }, [orgId]);

  const loadKeys = useCallback(async () => {
    setLoading(true);
    try {
      const personalResponse = await authApis.fetchPersonalApiKeys({ page_number: 0, page_size: 100 });
      if (personalResponse.ok) {
        setPersonalKeys(personalResponse.data.api_keys || []);
      } else {
        setPersonalKeys([]);
      }

      if (orgId) {
        const orgResponse = await authApis.fetchOrgApiKeys({
          org_id: orgId,
          page_number: 0,
          page_size: 100,
        });
        if (orgResponse.ok) {
          setOrgKeys(orgResponse.data.api_keys || []);
        } else {
          setOrgKeys([]);
        }
      } else {
        setOrgKeys([]);
      }
    } finally {
      setLoading(false);
    }
  }, [authApis, orgId]);

  useEffect(() => {
    loadKeys().catch(() => {});
  }, [loadKeys]);

  const rows = useMemo<KeyRow[]>(() => {
    const personalRows = personalKeys.map((key) => ({
      rowId: `personal:${key.api_key_id}`,
      apiKeyId: key.api_key_id,
      scope: "personal" as const,
      name: `Personal Key ${key.api_key_id.slice(0, 6)}`,
      createdAt: key.created_at,
      expiresAtSeconds: key.expires_at_seconds,
    }));

    const orgRows = orgKeys.map((key) => ({
      rowId: `org:${key.api_key_id}`,
      apiKeyId: key.api_key_id,
      scope: "org" as const,
      name: `Workspace Key ${key.api_key_id.slice(0, 6)}`,
      createdAt: key.created_at,
      expiresAtSeconds: key.expires_at_seconds,
    }));

    return [...orgRows, ...personalRows].sort((a, b) => b.createdAt - a.createdAt);
  }, [orgKeys, personalKeys]);

  const toggleKeyVisibility = (rowId: string) => {
    setShowKeys((previous) => {
      const next = new Set(previous);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  };

  const copyValue = async (value: string, successMessage: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(successMessage);
    } catch {
      toast.error("Unable to copy to clipboard.");
    }
  };

  const handleCreateKey = async () => {
    setLoading(true);
    try {
      const response = scope === "org" && orgId
        ? await authApis.createOrgApiKey(orgId, expiration)
        : await authApis.createPersonalApiKey(expiration);

      if (!response.ok) {
        toast.error("Unable to create key. Please try again later.");
        return;
      }

      setCreatedToken({
        id: response.data.api_key_id,
        token: response.data.api_key_token,
      });
      setCreateOpen(false);
      setCreatedTokenOpen(true);
      await loadKeys();
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteKey = async (apiKeyId: string) => {
    setLoading(true);
    try {
      const response = await authApis.deleteApiKey(apiKeyId);
      if (!response.ok) {
        toast.error("Unable to delete key. Please try again later.");
        return;
      }
      toast.success("API key deleted.");
      await loadKeys();
    } finally {
      setLoading(false);
    }
  };

  if (!canRead) {
    return (
      <div className="py-12 text-center">
        <p className="text-sm font-medium">API Keys</p>
        <p className="mt-1 text-xs text-muted-foreground">You don&apos;t have permission to view API keys.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-medium text-foreground">API Keys</h1>
          <p className="mt-1 text-[13px] text-muted-foreground">Manage your SDK and API keys</p>
        </div>
        {canWrite && (
          <Button size="sm" className="h-8 text-[12px]" onClick={() => setCreateOpen(true)} disabled={loading}>
            <Plus className="mr-1 h-3.5 w-3.5" /> Create Key
          </Button>
        )}
      </div>

      <table className="w-full">
        <thead>
          <tr className="border-b border-border">
            <th className="pb-2 text-left text-[11px] font-medium text-muted-foreground">Name</th>
            <th className="pb-2 text-left text-[11px] font-medium text-muted-foreground">Key</th>
            <th className="pb-2 text-left text-[11px] font-medium text-muted-foreground">Environment</th>
            <th className="pb-2 text-left text-[11px] font-medium text-muted-foreground">Last Used</th>
            <th className="w-20" />
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((row) => (
            <tr key={row.rowId} className="group transition-colors hover:bg-muted">
              <td className="py-2.5">
                <p className="text-[13px] font-medium text-foreground">{row.name}</p>
                <p className="text-[11px] text-muted-foreground">
                  Created {formatDate(row.createdAt)} · Expires {formatDate(row.expiresAtSeconds)}
                </p>
              </td>
              <td className="py-2.5">
                <div className="flex items-center gap-1.5">
                  <code className="font-mono text-[11px] text-muted-foreground">
                    {showKeys.has(row.rowId) ? row.apiKeyId : maskKey(row.apiKeyId)}
                  </code>
                  <button
                    onClick={() => toggleKeyVisibility(row.rowId)}
                    className="text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {showKeys.has(row.rowId) ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                  </button>
                  <button
                    onClick={() => {
                      copyValue(row.apiKeyId, "API key ID copied.");
                    }}
                    className="text-muted-foreground transition-colors hover:text-foreground"
                  >
                    <Copy className="h-3 w-3" />
                  </button>
                </div>
              </td>
              <td className="py-2.5 text-[13px] text-muted-foreground">
                {row.scope === "org" ? "workspace" : "personal"}
              </td>
              <td className="py-2.5 text-[13px] text-muted-foreground">—</td>
              <td className="py-2.5">
                {canWrite && (
                  <button
                    className="text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
                    onClick={() => {
                      handleDeleteKey(row.apiKeyId).catch(() => {});
                    }}
                    disabled={loading}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {rows.length === 0 && !loading ? (
        <div className="rounded-lg border border-border py-10 text-center text-[13px] text-muted-foreground">
          No API keys found.
        </div>
      ) : null}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create API Key</DialogTitle>
            <DialogDescription>
              Choose key scope and expiration. You will only see the full key token once.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <p className="text-[12px] font-medium text-foreground">Scope</p>
              <Select value={scope} onValueChange={(value) => setScope(value as KeyScope)}>
                <SelectTrigger className="h-9 text-[13px]">
                  <SelectValue placeholder="Select scope" />
                </SelectTrigger>
                <SelectContent>
                  {orgId ? <SelectItem value="org">Workspace</SelectItem> : null}
                  <SelectItem value="personal">Personal</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <p className="text-[12px] font-medium text-foreground">Expiration</p>
              <Select value={expiration} onValueChange={(value) => setExpiration(value as ApiKeyExpirationOption)}>
                <SelectTrigger className="h-9 text-[13px]">
                  <SelectValue placeholder="Select expiration" />
                </SelectTrigger>
                <SelectContent>
                  {EXPIRATION_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={() => { handleCreateKey().catch(() => {}); }} disabled={loading}>
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={createdTokenOpen} onOpenChange={setCreatedTokenOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save this key now</DialogTitle>
            <DialogDescription>
              This token will not be shown again after closing this dialog.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-md border border-border bg-muted/50 p-3">
            <p className="mb-2 text-[12px] font-medium text-foreground">API Key Token</p>
            <code className="break-all text-[12px] text-foreground">{createdToken?.token || "—"}</code>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                if (!createdToken?.token) return;
                copyValue(createdToken.token, "API key token copied.");
              }}
            >
              Copy token
            </Button>
            <Button onClick={() => setCreatedTokenOpen(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApiKeysSettingsPage;
